import { useEffect, useMemo, useState } from 'react'
import { SeccionPlegable } from '@/components/ui/seccion-plegable'
import { useParams } from 'react-router-dom'
import { AlertCircle, Info, KeyRound, Loader2, Settings2 } from 'lucide-react'

import { AutomationTemplateMetaAlerts } from '@/components/settings/AutomationTemplateMetaAlerts'
import {
  buildWebPortalVariableMapping,
  normalizeWebPortalMappingForApi,
  normalizeWebPortalMappingFromApi,
} from '@/components/settings/webPortalTemplateMapping'
import { TemplateVariablesHelpDialog } from '@/components/actions/TemplateVariablesHelpDialog'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { META_INTEGRATION_ENABLED } from '@/config/env'
import { useAuth } from '@/hooks/use-auth'
import { log } from '@/lib/logger'
import { useWhatsAppStatusQuery } from '@/queries'
import { whatsappService, type WABA, type WhatsAppTemplate } from '@/services/whatsappService'
import {
  webPortalTemplatesService,
  type WebPortalAvailableField,
  type WebPortalTemplateConfig,
  type WebPortalVariableMappingEntry,
} from '@/services/webPortalTemplatesService'

type SendType = 'auth_otp'

type SendTypeMeta = {
  send_type: SendType
  title: string
  description: string
  icon: typeof KeyRound
  expected: number
}

const SEND_TYPES: SendTypeMeta[] = [
  {
    send_type: 'auth_otp',
    title: 'Código de verificación',
    description: 'Manda por WhatsApp el código de 6 dígitos con el que el usuario entra al portal.',
    icon: KeyRound,
    expected: 1,
  },
]

const HELP_VARIABLES = [
  {
    slot: '{{1}} (opción "code")',
    label: 'Código de verificación',
    source: 'Portal → código de 6 dígitos generado para ese intento de acceso',
    example: '483920',
  },
]

// Meta's Authentication templates are fixed-format: the body text is not exposed
// by the API, so there are no {{n}} to count — the single parameter is always the
// code, and the OTP button (copy code / autofill) is added by Meta.
function isAuthenticationTemplate(template: WhatsAppTemplate): boolean {
  return (template.category || '').toUpperCase() === 'AUTHENTICATION'
}

type RowState = {
  template_name: string
  template_language: string
  waba_id: string
  enabled: boolean
  variable_mapping: WebPortalVariableMappingEntry[]
  available_fields: WebPortalAvailableField[]
  toggling: boolean
}

const emptyRow = (): RowState => ({
  template_name: '',
  template_language: 'es_ES',
  waba_id: '',
  enabled: false,
  variable_mapping: [],
  available_fields: [],
  toggling: false,
})

type DialogDraft = {
  waba_id: string
  template_key: string
  variable_mapping: WebPortalVariableMappingEntry[]
}

const EMPTY_DRAFT: DialogDraft = {
  waba_id: '',
  template_key: '',
  variable_mapping: [],
}

export function AuthOtpNotificationsCard() {
  const { lang = 'es' } = useParams()
  const { token } = useAuth()
  const hasAuthToken = Boolean(token && token.trim())
  const [isOpen, setIsOpen] = useState(false)

  const { data: whatsappStatus, isLoading: whatsappStatusLoading } = useWhatsAppStatusQuery({
    enabled: META_INTEGRATION_ENABLED && hasAuthToken,
  })
  const whatsappConnected = Boolean(whatsappStatus?.connected)
  const canConfigureTemplates = META_INTEGRATION_ENABLED && whatsappConnected

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [helpOpen, setHelpOpen] = useState(false)

  const [rows, setRows] = useState<Record<SendType, RowState>>({
    auth_otp: emptyRow(),
  })

  const [activeSendType, setActiveSendType] = useState<SendType | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [dialogLoading, setDialogLoading] = useState(false)
  const [dialogError, setDialogError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [draft, setDraft] = useState<DialogDraft>(EMPTY_DRAFT)
  const [wabas, setWabas] = useState<WABA[]>([])
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([])

  const activeMeta = useMemo(
    () => SEND_TYPES.find((item) => item.send_type === activeSendType) ?? null,
    [activeSendType],
  )
  const activeRow = activeSendType ? rows[activeSendType] : null
  const draftTemplate = useMemo(
    () => templates.find((item) => `${item.name}:${item.language}` === draft.template_key) ?? null,
    [templates, draft.template_key],
  )

  const applyConfig = (data: WebPortalTemplateConfig[]) => {
    setRows((prev) => {
      const next = { ...prev }
      for (const cfg of data) {
        if (cfg.send_type in next) {
          const sendType = cfg.send_type as SendType
          const meta = SEND_TYPES.find((item) => item.send_type === sendType)
          next[sendType] = {
            ...next[sendType],
            template_name: cfg.template_name,
            template_language: cfg.template_language || 'es_ES',
            waba_id: cfg.waba_id,
            enabled: cfg.enabled,
            variable_mapping: buildWebPortalVariableMapping(
              meta?.expected ?? 0,
              normalizeWebPortalMappingFromApi(cfg.variable_mapping),
            ),
            available_fields: cfg.available_fields || [],
          }
        }
      }
      return next
    })
  }

  const loadConfig = async () => {
    if (!hasAuthToken) {
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const response = await webPortalTemplatesService.getConfig()
      applyConfig(response.data || [])
    } catch (err) {
      const message = err instanceof Error ? err.message : 'No se pudo cargar la configuración'
      log.error('Auth OTP notifications config load failed', { message })
      setError(message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void loadConfig()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAuthToken, token])

  const loadTemplates = async (wabaId: string) => {
    setTemplates([])
    try {
      const data = await whatsappService.getTemplates(wabaId)
      setTemplates(data.filter(isAuthenticationTemplate))
    } catch (err) {
      const message = err instanceof Error ? err.message : 'No se pudieron cargar las plantillas'
      log.error('Auth OTP notifications template load failed', { message, wabaId })
      setDialogError(message)
    }
  }

  const openDialog = async (sendType: SendType) => {
    const meta = SEND_TYPES.find((item) => item.send_type === sendType)
    const row = rows[sendType]
    setActiveSendType(sendType)
    setDialogError(null)
    setDraft({
      waba_id: row.waba_id,
      template_key: row.template_name ? `${row.template_name}:${row.template_language}` : '',
      variable_mapping: row.variable_mapping,
    })
    setDialogOpen(true)
    setDialogLoading(true)
    try {
      const availableWabas = await whatsappService.getWabas()
      setWabas(availableWabas)
      const preferredWabaId = (row.waba_id && availableWabas.some((item) => item.id === row.waba_id)
        ? row.waba_id
        : availableWabas[0]?.id) || ''
      setDraft((prev) => ({ ...prev, waba_id: preferredWabaId }))
      if (!availableWabas.length) {
        setDialogError('No hay ninguna cuenta de WhatsApp conectada.')
      }
      if (preferredWabaId) {
        const loadedTemplates = (await whatsappService.getTemplates(preferredWabaId)).filter(
          isAuthenticationTemplate,
        )
        setTemplates(loadedTemplates)
        if (row.template_name) {
          setDraft((prev) => ({
            ...prev,
            variable_mapping: buildWebPortalVariableMapping(meta?.expected ?? 0, row.variable_mapping),
          }))
        }
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'No se pudieron cargar las cuentas de WhatsApp'
      log.error('Auth OTP notifications WABA load failed', { message })
      setDialogError(message)
    } finally {
      setDialogLoading(false)
    }
  }

  const handleWabaChange = async (value: string) => {
    setDraft((prev) => ({ ...prev, waba_id: value, template_key: '', variable_mapping: [] }))
    setDialogError(null)
    await loadTemplates(value)
  }

  const handleTemplateChange = (templateKey: string) => {
    setDraft((prev) => ({
      ...prev,
      template_key: templateKey,
      variable_mapping: buildWebPortalVariableMapping(activeMeta?.expected ?? 0, prev.variable_mapping),
    }))
  }

  const handleMappingChange = (position: number, sourceKey: string) => {
    setDraft((prev) => ({
      ...prev,
      variable_mapping: prev.variable_mapping.map((item) =>
        item.position === position ? { ...item, source_type: 'variable', source_key: sourceKey } : item,
      ),
    }))
  }

  const handleSaveDialog = async () => {
    if (!activeSendType || !activeMeta) return
    const selectedWaba = wabas.find((item) => item.id === draft.waba_id)
    const selectedTemplate = draftTemplate
    if (!selectedWaba || !selectedTemplate) {
      setDialogError('Selecciona una cuenta de WhatsApp y una plantilla.')
      return
    }
    if (!isAuthenticationTemplate(selectedTemplate)) {
      setDialogError('El código de verificación solo admite plantillas de categoría Authentication.')
      return
    }
    if (draft.variable_mapping.some((item) => !item.source_key)) {
      setDialogError('Mapea la variable de la plantilla al código que envía el portal.')
      return
    }

    setSaving(true)
    setDialogError(null)
    try {
      const response = await webPortalTemplatesService.putConfig({
        send_type: activeSendType,
        template_name: selectedTemplate.name,
        template_language: selectedTemplate.language,
        waba_id: selectedWaba.id,
        enabled: rows[activeSendType].enabled,
        variable_mapping: normalizeWebPortalMappingForApi(draft.variable_mapping),
        // El OTP no lleva botón URL: Meta añade el suyo (copiar código) en las
        // plantillas de categoría Authentication.
        button_response_mapping: {},
      })
      const saved = response.data
      setRows((prev) => ({
        ...prev,
        [activeSendType]: {
          ...prev[activeSendType],
          template_name: saved.template_name,
          template_language: saved.template_language || 'es_ES',
          waba_id: saved.waba_id,
          enabled: saved.enabled,
          variable_mapping: buildWebPortalVariableMapping(
            activeMeta.expected,
            normalizeWebPortalMappingFromApi(saved.variable_mapping),
          ),
          available_fields: saved.available_fields || prev[activeSendType].available_fields,
        },
      }))
      setDialogOpen(false)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'No se pudo guardar'
      log.error('Auth OTP notifications save failed', { message, sendType: activeSendType })
      setDialogError(message)
    } finally {
      setSaving(false)
    }
  }

  const handleToggle = async (sendType: SendType, enabled: boolean) => {
    const row = rows[sendType]
    if (!row.template_name) return
    setRows((prev) => ({ ...prev, [sendType]: { ...prev[sendType], toggling: true } }))
    setError(null)
    try {
      const response = await webPortalTemplatesService.putConfig({
        send_type: sendType,
        template_name: row.template_name,
        template_language: row.template_language,
        waba_id: row.waba_id,
        enabled,
        variable_mapping: normalizeWebPortalMappingForApi(row.variable_mapping),
      })
      const saved = response.data
      setRows((prev) => ({
        ...prev,
        [sendType]: { ...prev[sendType], enabled: saved.enabled, toggling: false },
      }))
    } catch (err) {
      const message = err instanceof Error ? err.message : 'No se pudo actualizar el estado'
      log.error('Auth OTP notifications toggle failed', { message, sendType })
      setError(message)
      setRows((prev) => ({ ...prev, [sendType]: { ...prev[sendType], toggling: false } }))
    }
  }

  const anyEnabled = useMemo(
    () => SEND_TYPES.some((meta) => rows[meta.send_type].enabled),
    [rows],
  )

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center gap-2 py-8 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Cargando código de verificación...
        </CardContent>
      </Card>
    )
  }

  return (
    <>
      <SeccionPlegable
        numero="09"
        etiqueta="Acceso al portal"
        titulo="Código de verificación"
        descripcion="Plantilla y estado del WhatsApp con el código de un solo uso que el portal manda al iniciar sesión."
        estado={
          !canConfigureTemplates ? (
            <Badge variant="outline" className="ml-1 border-warning/40 text-warning">
              Vista local
            </Badge>
          ) : anyEnabled ? (
            <Badge variant="outline" className="ml-1 border-success/40 text-success">
              Activa
            </Badge>
          ) : (
            <Badge variant="outline" className="ml-1">
              Pausada
            </Badge>
          )
        }
        abierta={isOpen}
        onAlternar={() => setIsOpen((prev) => !prev)}
        acciones={
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            className="shrink-0 rounded-full text-muted-foreground"
            aria-label="Ayuda variables código de verificación"
            onClick={() => setHelpOpen(true)}
          >
            <Info className="h-4 w-4" />
          </Button>
        }
      >
              <AutomationTemplateMetaAlerts
                lang={lang}
                metaIntegrationEnabled={META_INTEGRATION_ENABLED}
                whatsappConnected={whatsappConnected}
                whatsappStatusLoading={whatsappStatusLoading}
              />

              {error ? (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              ) : null}

              <div className="space-y-2">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Notificación</TableHead>
                      <TableHead>Plantilla</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead className="text-right">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {SEND_TYPES.map((meta) => {
                      const row = rows[meta.send_type]
                      const RowIcon = meta.icon
                      return (
                        <TableRow key={meta.send_type}>
                          <TableCell>
                            <div className="flex items-center gap-2 font-medium text-foreground">
                              <RowIcon className="h-4 w-4 text-primary" />
                              {meta.title}
                            </div>
                            <p className="mt-0.5 text-xs text-muted-foreground">{meta.description}</p>
                          </TableCell>
                          <TableCell>
                            {row.template_name ? (
                              <>
                                <div className="font-medium">{row.template_name}</div>
                                <div className="text-xs text-muted-foreground">{row.template_language}</div>
                              </>
                            ) : (
                              <span className="text-xs text-muted-foreground">Sin configurar</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Switch
                                checked={row.enabled}
                                disabled={!canConfigureTemplates || !row.template_name || row.toggling}
                                onCheckedChange={(checked) => void handleToggle(meta.send_type, checked)}
                              />
                              <span className="text-xs text-muted-foreground">
                                {row.enabled ? 'Activo' : 'Pausado'}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={!canConfigureTemplates}
                              onClick={() => void openDialog(meta.send_type)}
                            >
                              <Settings2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </div>
      </SeccionPlegable>

      <TemplateVariablesHelpDialog
        open={helpOpen}
        onOpenChange={setHelpOpen}
        title="Variables del código de verificación"
        description="El portal manda un único dato, el código de 6 dígitos; aquí solo eliges qué plantilla aprobada recibe el hueco {{1}} y si el envío está activo."
        orderNote="Este aviso no va asociado a ninguna propiedad ni a ningún usuario del CRM: solo al teléfono que pide el código. El enlace de acceso directo al portal sigue yendo por email."
        variablesTitle="Variables disponibles para {{1}}"
        sourceLabel="Origen:"
        variables={HELP_VARIABLES}
        examples={[
          {
            title: 'Código de verificación',
            text: '{{1}} es tu código de verificación. Por tu seguridad, no lo compartas.',
          },
        ]}
        note="Solo se pueden enlazar plantillas de categoría Authentication. Son de formato fijo: el texto lo pone Meta, la única variable es el código y el botón de copiar código lo añade WhatsApp. Se crean en WhatsApp Manager con Autenticación → Código de acceso de un solo uso."
      />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{activeMeta?.title ?? 'Configurar plantilla'}</DialogTitle>
          </DialogHeader>

          {dialogLoading ? (
            <div className="flex items-center gap-2 py-8 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Cargando plantillas de Meta...
            </div>
          ) : (
            <div className="space-y-4">
              {dialogError ? (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{dialogError}</AlertDescription>
                </Alert>
              ) : null}

              <div className="space-y-2">
                <Label>Cuenta de WhatsApp</Label>
                <Select value={draft.waba_id || undefined} onValueChange={(value) => void handleWabaChange(value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar WABA" />
                  </SelectTrigger>
                  <SelectContent>
                    {wabas.map((waba) => (
                      <SelectItem key={waba.id} value={waba.id}>
                        {waba.name || waba.id}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Plantilla de autenticación aprobada</Label>
                <Select
                  value={draft.template_key || undefined}
                  onValueChange={handleTemplateChange}
                  disabled={!draft.waba_id || templates.length === 0}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar plantilla" />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map((template) => (
                      <SelectItem
                        key={`${template.name}:${template.language}`}
                        value={`${template.name}:${template.language}`}
                      >
                        {template.name} ({template.language})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {draft.waba_id && templates.length === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    Esta cuenta no tiene ninguna plantilla de categoría Authentication aprobada. Créala en WhatsApp
                    Manager (Autenticación → Código de acceso de un solo uso) y vuelve aquí cuando Meta la apruebe.
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Solo se listan plantillas de categoría Authentication: son las únicas que llevan el botón de copiar
                    código que WhatsApp exige para un OTP.
                  </p>
                )}
              </div>

              {draft.variable_mapping.length > 0 ? (
                <div className="space-y-2">
                  <Label>Mapa de variables</Label>
                  <p className="text-xs text-muted-foreground">
                    La plantilla lleva un único parámetro, el código que envía el portal web.
                  </p>
                  {draft.variable_mapping.map((mapping) => (
                    <div key={mapping.position} className="grid grid-cols-[64px_1fr] items-center gap-2">
                      <span className="text-sm font-medium">{`{{${mapping.position}}}`}</span>
                      <Select
                        value={mapping.source_key || undefined}
                        onValueChange={(value) => handleMappingChange(mapping.position, value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar dato del portal" />
                        </SelectTrigger>
                        <SelectContent>
                          {(activeRow?.available_fields ?? []).map((field) => (
                            <SelectItem key={field.key} value={field.key}>
                              {field.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              type="button"
              disabled={saving || dialogLoading || !draft.template_key}
              onClick={() => void handleSaveDialog()}
            >
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
